import React from 'react';
import {
	Box,
	Button,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Select,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	LinearProgress,
	CircularProgress,
	Skeleton,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GPDAReassignState } from 'containers/pages/cases/hooks/useGPDAReassign';
import { ActivityMapping } from 'containers/pages/cases/utils/matchActivities';

interface GPDAReassignDialogProps {
	state: GPDAReassignState;
	onUpdateMapping: (oldActivityId: number, newActivityId: number) => void;
	onApply: () => void;
	onClose: () => void;
}

const confidenceLabel: Record<ActivityMapping['confidence'], { label: string; color: 'success' | 'info' | 'secondary' | 'warning' | 'error' }> = {
	exact: { label: 'Exact match', color: 'success' },
	contains: { label: 'Partial match', color: 'success' },
	ai: { label: 'AI match', color: 'info' },
	single: { label: 'Only option', color: 'secondary' },
	manual: { label: 'Manual', color: 'warning' },
};

function GPDAReassignDialog({ state, onUpdateMapping, onApply, onClose }: GPDAReassignDialogProps) {
	const { open, loading, applying, caseTitle, target, comments, newActivities, mappings, progress } = state;
	const allMapped = mappings.every((m) => m.newActivityId !== null);
	const totalComments = comments.length;

	return (
		<Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				Reassign Comment Activities
			</DialogTitle>

			<DialogContent dividers>
				{loading ? (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
						<Skeleton variant="rounded" height={24} width="60%" />
						<Skeleton variant="rounded" height={24} width="80%" />
						<Skeleton variant="rounded" height={200} />
						<Typography variant="body2" color="text.secondary" align="center">
							Analyzing activities and building smart mappings...
						</Typography>
					</Box>
				) : applying ? (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 4, alignItems: 'center' }}>
						<CircularProgress />
						<Typography variant="body1">
							Updating comments... {progress.done} / {progress.total}
						</Typography>
						<LinearProgress
							variant="determinate"
							value={progress.total > 0 ? (progress.done / progress.total) * 100 : 0}
							sx={{ width: '100%', height: 8, borderRadius: 4 }}
						/>
					</Box>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						{/* Case info */}
						<Box>
							<Typography variant="subtitle2" color="text.secondary">Case</Typography>
							<Typography variant="body1" fontWeight={600}>{caseTitle}</Typography>
						</Box>

						{/* GPD target */}
						{target && (
							<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
								<Typography variant="subtitle2" color="text.secondary">New GPD:</Typography>
								<Chip label={target.groupTitle} size="small" variant="outlined" />
								<ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
								<Chip label={target.projectTitle} size="small" variant="outlined" />
								<ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
								<Chip label={target.deliverableTitle} size="small" variant="outlined" />
							</Box>
						)}

						<Typography variant="body2" color="text.secondary">
							{totalComments} comment{totalComments !== 1 ? 's' : ''} will be updated.
							Map each old activity to a new one below.
						</Typography>

						{/* Mapping table */}
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Old Activity</TableCell>
										<TableCell align="center">Comments</TableCell>
										<TableCell />
										<TableCell>New Activity</TableCell>
										<TableCell align="center">Confidence</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{mappings.map((mapping) => {
										const conf = confidenceLabel[mapping.confidence];
										return (
											<TableRow key={mapping.oldActivity.id}>
												<TableCell>
													<Typography variant="body2" fontWeight={500}>
														{mapping.oldActivity.title}
													</Typography>
												</TableCell>
												<TableCell align="center">
													<Chip label={mapping.commentCount} size="small" />
												</TableCell>
												<TableCell align="center" sx={{ px: 0.5 }}>
													<ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
												</TableCell>
												<TableCell>
													<Select
														size="small"
														value={mapping.newActivityId ?? ''}
														onChange={(e) => onUpdateMapping(mapping.oldActivity.id, Number(e.target.value))}
														displayEmpty
														fullWidth
														sx={{ minWidth: 180 }}
													>
														<MenuItem value="" disabled>
															<em>Select activity...</em>
														</MenuItem>
														{newActivities.map((a) => (
															<MenuItem key={a.id} value={a.id}>
																{a.title}
															</MenuItem>
														))}
													</Select>
												</TableCell>
												<TableCell align="center">
													<Chip
														label={conf.label}
														size="small"
														color={conf.color}
														variant="outlined"
														sx={{ fontSize: '0.7rem' }}
													/>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}
			</DialogContent>

			{!loading && !applying && (
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button onClick={onClose} variant="outlined">
						Cancel
					</Button>
					<Button
						onClick={onApply}
						variant="contained"
						disabled={!allMapped}
					>
						Apply Mapping ({totalComments} comment{totalComments !== 1 ? 's' : ''})
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
}

export default GPDAReassignDialog;
